<template>
  <div class="whitelist-rule-panel" :class="{'embedded-mode': isEmbedded}">
    <div v-if="!isEmbedded" class="panel-header">
      <h3 class="panel-title">
        <i class="el-icon-shield mr-2"></i>
        船舶白名单规则管理
      </h3>
      <div class="panel-controls">
        <i class="el-icon-close" @click="closePanel" title="关闭"></i>
      </div>
    </div>
    
    <div class="panel-body">
      <div class="panel-toolbar" v-if="!isEmbedded">
        <el-button 
          type="primary" 
          size="small" 
          icon="el-icon-plus"
          @click="handleAddRule">
          新建规则
        </el-button>
        
        <div class="flex-spacer"></div>
      </div>
      
      <div class="panel-toolbar embedded-toolbar" v-if="isEmbedded">
        <div class="flex-spacer"></div>
        
        <el-input
          placeholder="搜索规则名称"
          v-model="searchQuery"
          class="search-input"
          size="small"
          prefix-icon="el-icon-search"
          clearable>
        </el-input>
      </div>
      
      <el-table
        :data="filteredRules"
        style="width: 100%"
        :size="isEmbedded ? 'mini' : 'small'"
        v-loading="loading"
        :max-height="isEmbedded ? 300 : 400"
        border>
        <el-table-column prop="ruleName" label="规则名称" width="200"></el-table-column>
        <el-table-column prop="ruleConfig.matchType" label="匹配类型" width="120">
          <template slot-scope="scope">
            {{ getMatchTypeLabel(scope.row.ruleConfig?.matchType) }}
          </template>
        </el-table-column>
        <el-table-column prop="ruleConfig.description" label="规则描述" show-overflow-tooltip></el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="160">
          <template slot-scope="scope">
            {{ $options.filters.dateFormat(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="120">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="small" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    
    <!-- 白名单规则编辑抽屉 -->
    <el-drawer
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      direction="rtl"
      size="600px"
      :show-close="true"
      :wrapper-closable="false"
      append-to-body>
      <div class="drawer-content">
        <el-form 
          :model="form" 
          :rules="formRules" 
          ref="ruleForm" 
          label-width="120px"
          size="small"
          class="drawer-form">
          
          <!-- 规则名称 -->
          <el-form-item label="规则名称" prop="ruleName">
            <el-input 
              v-model="form.ruleName" 
              placeholder="请输入规则名称">
            </el-input>
          </el-form-item>
          
          <!-- 匹配类型 -->
          <el-form-item label="匹配类型" prop="matchType">
            <el-select 
              v-model="form.matchType" 
              placeholder="请选择匹配类型"
              @change="handleMatchTypeChange"
              style="width: 100%">
              <el-option
                v-for="option in matchTypeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value">
              </el-option>
            </el-select>
          </el-form-item>
          
          <!-- 规则配置组件 -->
          <div v-if="form.matchType">
            <whitelist-rule-config
              :match-type="form.matchType"
              :config="ruleConfig"
              :vessel-type-options="vesselTypeOptions"
              @config-change="handleRuleConfigChange">
            </whitelist-rule-config>
          </div>
          
          
          <!-- 规则描述 -->
          <el-form-item label="规则描述" prop="description">
            <el-input 
              type="textarea" 
              v-model="form.description" 
              rows="3"
              placeholder="请输入规则描述">
            </el-input>
          </el-form-item>
        </el-form>
        
        <div class="drawer-footer">
          <el-button @click="dialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import {
  createWhitelistRule,
  getWhitelistRulesByBridgeId,
  getWhitelistRuleById,
  updateWhitelistRule,
  deleteWhitelistRule,
  updateWhitelistRuleStatus,
  WHITELIST_MATCH_TYPE_OPTIONS
} from '@/api/vessel-whitelist-rule';
import { getAllVesselTypes } from '@/api/vessel';
import { formatDate } from '@/utils/dateUtils';
import WhitelistRuleConfig from './WhitelistRuleConfig.vue';

export default {
  name: 'VesselWhitelistRulePanel',
  
  components: {
    WhitelistRuleConfig
  },
  
  filters: {
    dateFormat(value) {
      return formatDate(value);
    }
  },
  
  props: {
    bridgeId: {
      type: [String, Number],
      required: true
    },
    isEmbedded: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      loading: false,
      submitting: false,
      searchQuery: '',
      
      // 规则列表
      rules: [],
      
      // 匹配类型选项
      matchTypeOptions: WHITELIST_MATCH_TYPE_OPTIONS,
      
      // 船舶类型选项
      vesselTypeOptions: [],
      
      // 表单数据
      form: {
        id: null,
        bridgeId: null,
        ruleName: '',
        matchType: '',
        vesselTypes: [],
        mmsiPattern: '',
        vesselNames: [],
        caseSensitive: false,
        priority: 3, // 默认优先级：中等
        description: '',
        status: 1
      },
      
      // 船舶名称文本（用于textarea显示）
      vesselNamesText: '',
      
      // 规则配置对象
      ruleConfig: {
        vesselTypes: [],
        mmsiPattern: '',
        vesselNames: [],
        caseSensitive: false
      },
      
      // 表单校验规则
      formRules: {
        ruleName: [
          { required: true, message: '请输入规则名称', trigger: 'blur' },
          { max: 100, message: '规则名称长度不能超过100个字符', trigger: 'blur' }
        ],
        matchType: [
          { required: true, message: '请选择匹配类型', trigger: 'change' }
        ],
        vesselTypes: [
          { 
            validator: (rule, value, callback) => {
              if (this.form.matchType === 'VESSEL_TYPE' && (!value || value.length === 0)) {
                callback(new Error('请选择至少一个船舶类型'));
              } else {
                callback();
              }
            }, 
            trigger: 'change' 
          }
        ],
        mmsiPattern: [
          { 
            validator: (rule, value, callback) => {
              if (this.form.matchType === 'MMSI_PATTERN' && (!value || value.trim() === '')) {
                callback(new Error('请输入MMSI匹配模式'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        vesselNames: [
          { 
            validator: (rule, value, callback) => {
              if (this.form.matchType === 'NAME_PATTERN' && (!this.vesselNamesText || this.vesselNamesText.trim() === '')) {
                callback(new Error('请输入至少一个船舶名称'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ]
      },
      
      // 对话框控制
      dialogVisible: false,
      dialogTitle: '新建白名单规则',
      isEdit: false
    };
  },
  
  computed: {
    // 过滤后的规则列表
    filteredRules() {
      if (!this.searchQuery) {
        return this.rules;
      }
      
      const query = this.searchQuery.toLowerCase();
      return this.rules.filter(rule => {
        return rule.ruleName && rule.ruleName.toLowerCase().includes(query);
      });
    }
  },
  
  watch: {
    bridgeId: {
      handler(newVal) {
        if (newVal) {
          this.loadRules();
        }
      },
      immediate: true
    },
    
    // 监听船舶名称文本变化，同步到表单数组
    vesselNamesText(newVal) {
      if (newVal) {
        this.form.vesselNames = newVal.split('\n').filter(name => name.trim() !== '');
      } else {
        this.form.vesselNames = [];
      }
    }
  },
  
  created() {
    // 加载船舶类型数据
    this.loadVesselTypes();
  },
  
  methods: {
    // 加载白名单规则列表
    async loadRules() {
      if (!this.bridgeId) return;
      
      this.loading = true;
      try {
        const response = await getWhitelistRulesByBridgeId(this.bridgeId);
        
        if (response && response.code === 200) {
          this.rules = response.data || [];
          console.log(`成功加载白名单规则，共${this.rules.length}条记录`);
        } else {
          const errorMsg = response?.message || '获取白名单规则失败';
          console.warn('API响应异常:', response);
          this.$message.error(errorMsg);
        }
      } catch (error) {
        console.error('加载白名单规则失败:', error);
        this.$message.error('加载白名单规则失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 加载船舶类型选项
    async loadVesselTypes() {
      try {
        const response = await getAllVesselTypes();
        
        if (response && response.code === 200) {
          this.vesselTypeOptions = response.data || [];
          console.log('成功加载船舶类型选项，共', this.vesselTypeOptions.length, '条');
        } else {
          console.warn('获取船舶类型选项响应异常:', response);
        }
      } catch (error) {
        console.error('加载船舶类型选项失败:', error);
      }
    },
    
    // 获取匹配类型标签
    getMatchTypeLabel(matchType) {
      const option = this.matchTypeOptions.find(opt => opt.value === matchType);
      return option ? option.label : matchType;
    },
    
    
    // 关闭面板
    closePanel() {
      this.$emit('close');
    },
    
    // 添加规则
    handleAddRule() {
      this.isEdit = false;
      this.dialogTitle = '新建白名单规则';
      this.resetForm();
      this.form.bridgeId = this.bridgeId;
      this.dialogVisible = true;
    },
    
    // 编辑规则
    async handleEdit(row) {
      this.isEdit = true;
      this.dialogTitle = '编辑白名单规则';
      this.resetForm();
      
      try {
        const response = await getWhitelistRuleById(row.id);
        
        if (response && response.code === 200) {
          const ruleData = response.data;
          if (ruleData) {
            this.form = {
              id: ruleData.id,
              bridgeId: ruleData.bridgeId,
              ruleName: ruleData.ruleName,
              matchType: ruleData.ruleConfig?.matchType || '',
              vesselTypes: ruleData.ruleConfig?.vesselTypes || [],
              mmsiPattern: ruleData.ruleConfig?.pattern || ruleData.ruleConfig?.mmsiPattern || '',
              vesselNames: ruleData.ruleConfig?.patterns || ruleData.ruleConfig?.vesselNames || [],
              caseSensitive: false, // 默认不区分大小写
              priority: ruleData.priority || 3, // 默认优先级：中等
              description: ruleData.ruleConfig?.description || '',
              status: ruleData.status
            };
            
            // 设置船舶名称文本
            if (this.form.vesselNames && this.form.vesselNames.length > 0) {
              this.vesselNamesText = this.form.vesselNames.join('\n');
            }
            
            // 设置规则配置
            this.ruleConfig = {
              vesselTypes: this.form.vesselTypes,
              mmsiPattern: this.form.mmsiPattern,
              vesselNames: this.form.vesselNames,
              caseSensitive: ruleData.ruleConfig?.caseSensitive || false
            };
            
            console.log('成功获取白名单规则详情:', this.form.ruleName);
          } else {
            this.$message.warning('未找到白名单规则详情');
          }
        } else {
          const errorMsg = response?.message || '获取规则详情失败';
          console.warn('API响应异常:', response);
          this.$message.error(errorMsg);
        }
      } catch (error) {
        console.error('获取规则详情失败:', error);
        this.$message.error('获取规则详情失败');
      }
      
      this.dialogVisible = true;
    },
    
    // 删除规则
    handleDelete(row) {
      this.$confirm('确认要删除该白名单规则吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await deleteWhitelistRule(row.id);
          
          if (response && response.code === 200) {
            this.$message.success(response.message || '删除成功');
            this.loadRules();
          } else {
            const errorMsg = response?.message || '删除失败';
            console.warn('API响应异常:', response);
            this.$message.error(errorMsg);
          }
        } catch (error) {
          console.error('删除规则失败:', error);
          this.$message.error('删除规则失败');
        }
      }).catch(() => {
        // 用户取消删除操作
      });
    },
    
    
    // 匹配类型变化处理
    handleMatchTypeChange() {
      // 清空相关配置
      this.form.vesselTypes = [];
      this.form.mmsiPattern = '';
      this.form.vesselNames = [];
      this.vesselNamesText = '';
      
      // 重置规则配置
      this.ruleConfig = {
        vesselTypes: [],
        mmsiPattern: '',
        vesselNames: [],
        caseSensitive: false
      };
    },
    
    // 处理规则配置变化
    handleRuleConfigChange(config) {
      this.ruleConfig = { ...config };
      
      // 同步到表单数据
      this.form.vesselTypes = config.vesselTypes || [];
      this.form.mmsiPattern = config.mmsiPattern || '';
      this.form.vesselNames = config.vesselNames || [];
      this.form.caseSensitive = false; // 默认不区分大小写
      
      // 同步船舶名称文本（用于表单验证）
      if (config.vesselNames && config.vesselNames.length > 0) {
        this.vesselNamesText = config.vesselNames.join('\n');
      } else {
        this.vesselNamesText = '';
      }
    },
    
    // 重置表单
    resetForm() {
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.resetFields();
      }
      
      this.form = {
        id: null,
        bridgeId: this.bridgeId,
        ruleName: '',
        matchType: '',
        vesselTypes: [],
        mmsiPattern: '',
        vesselNames: [],
        caseSensitive: false,
        priority: 3, // 默认优先级：中等
        description: '',
        status: 1
      };
      
      this.vesselNamesText = '';
      
      // 重置规则配置
      this.ruleConfig = {
        vesselTypes: [],
        mmsiPattern: '',
        vesselNames: [],
        caseSensitive: false
      };
    },
    
    // 提交表单
    submitForm() {
      this.$refs.ruleForm.validate(async (valid) => {
        if (!valid) return;
        
        this.submitting = true;
        try {
          let response;
          const actionType = this.isEdit ? '更新' : '创建';
          
          // 准备提交数据
          const ruleConfig = {
            matchType: this.form.matchType,
            description: this.form.description
          };
          
          // 根据匹配类型设置配置
          if (this.form.matchType === 'VESSEL_TYPE') {
            ruleConfig.vesselTypes = this.form.vesselTypes;
          } else if (this.form.matchType === 'MMSI_PATTERN') {
            ruleConfig.pattern = this.form.mmsiPattern; // 注意：服务端字段名是pattern，不是mmsiPattern
          } else if (this.form.matchType === 'NAME_PATTERN') {
            ruleConfig.patterns = this.form.vesselNames; // 注意：服务端字段名是patterns
            ruleConfig.caseSensitive = this.form.caseSensitive;
          }
          
          const submitData = {
            id: this.form.id,
            bridgeId: this.form.bridgeId,
            ruleName: this.form.ruleName,
            priority: this.form.priority,
            status: this.form.status,
            ruleConfig: ruleConfig
          };
          
          if (this.isEdit) {
            // 编辑模式
            response = await updateWhitelistRule(this.form.id, submitData);
          } else {
            // 添加模式
            response = await createWhitelistRule(submitData);
          }
          
          if (response && response.code === 200) {
            this.$message.success(response.message || `${actionType}成功`);
            this.dialogVisible = false;
            this.loadRules();
          } else {
            const errorMsg = response?.message || `${actionType}失败`;
            console.warn(`${actionType}规则API响应异常:`, response);
            this.$message.error(errorMsg);
          }
        } catch (error) {
          const actionType = this.isEdit ? '更新' : '创建';
          console.error(`${actionType}规则失败:`, error);
          this.$message.error(`${actionType}失败`);
        } finally {
          this.submitting = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.whitelist-rule-panel {
  width: 900px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
}

.whitelist-rule-panel.embedded-mode {
  width: 100%;
  background-color: transparent;
  box-shadow: none;
  margin-bottom: 0;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  border-bottom: 1px solid #ebeef5;
  cursor: pointer;
  user-select: none;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  color: #2c5aa0;
}

.panel-controls {
  display: flex;
  align-items: center;
}

.panel-body {
  padding: 15px;
  max-height: 600px;
  overflow-y: auto;
}

.embedded-mode .panel-body {
  padding: 0;
  max-height: 500px;
}

.panel-toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.embedded-toolbar {
  margin-bottom: 10px;
}

.flex-spacer {
  flex-grow: 1;
}

.search-input {
  width: 200px;
  margin-right: 10px;
}

.mr-2 {
  margin-right: 8px;
}

/* 抽屉面板样式 */
.drawer-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.drawer-form {
  flex: 1;
  overflow-y: auto;
}

.drawer-footer {
  margin-top: 20px;
  text-align: right;
  padding-top: 10px;
  border-top: 1px solid #ebeef5;
}

/* 表单提示样式 */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.4;
}

/* 优先级标签样式 */
.el-tag {
  font-weight: bold;
}
</style>
